import React, { useMemo, useState } from 'react';
import {
  Alert, Box, Button, InputAdornment, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel, TextField, Typography
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip as ChartTooltip, XAxis, YAxis } from 'recharts';
import {
  AXIS_TICK, BORDER, ChartCard, ChartSkeleton, clip, NoData, ShareBar, Stat, StatStrip, SubText, TABLE_SX, TipBox, TipRow, TooltipInjected, tooltipPoint
} from '../../OnlineJournal/components/analytics/chartKit';
import { CHART } from '../../OnlineJournal/constants';
import { deltaPct, fmtDay, fmtDelta, fmtInt, fmtPct, share } from '../../OnlineJournal/format';
import { useLoad } from '../../OnlineJournal/hooks/useLoad';
import { GROUP_COLORS, WATCH_COLORS } from '../constants';
import { plural } from '../format';
import { DocumentFilters } from '../types';
import { analyticsApi } from './analyticsApi';
import { fmtHours, KIND_COLORS } from './analyticsFormat';
import { MiniToggle } from './analyticsKit';
import { Period, PharmaciesResponse, PharmacyStats, ViewProps } from './analyticsTypes';

type Metric = 'total' | 'errors' | 'warnings' | 'drafts' | 'p50OutHours' | 'p50InHours';
type KindFilter = 'all' | 'pharmacy' | 'warehouse';

const METRICS: Array<{ value: Metric; label: string; title: string; subtitle: string }> = [
  { value: 'total', label: 'Документы', title: 'по числу документов', subtitle: 'Синий - отправлено, бирюзовый - получено, серый - всего в прошлом периоде.' },
  { value: 'errors', label: 'Ошибки', title: 'по документам с ошибками', subtitle: 'Документы периода, которые сейчас в статусах ошибки.' },
  { value: 'warnings', label: 'На контроле', title: 'по документам на контроле', subtitle: 'Документы периода, которые скоро отменит или скроет автоочистка.' },
  { value: 'drafts', label: 'Черновики', title: 'по черновикам', subtitle: 'Документы периода, которые так и остались черновиками.' },
  { value: 'p50OutHours', label: 'Срок отправки', title: 'по медиане до «Завершен» как отправитель', subtitle: 'Медиана от создания до «Завершен» по документам, где аптека - отправитель. Сверху - самые медленные.' },
  { value: 'p50InHours', label: 'Срок приёмки', title: 'по медиане до «Завершен» как получатель', subtitle: 'Медиана от создания до «Завершен» по документам, где аптека - получатель. Сверху - самые медленные.' }
];

const METRIC_COLORS: Record<Metric, string> = {
  total: KIND_COLORS.direct,
  errors: GROUP_COLORS.error,
  warnings: WATCH_COLORS.warn,
  drafts: GROUP_COLORS.draft,
  p50OutHours: '#64748b',
  p50InHours: '#64748b'
};

/** Какие документы аптеки открывает клик при выбранной метрике */
const METRIC_PATCH: Record<Metric, Partial<DocumentFilters>> = {
  total: {},
  errors: { statuses: ['error'] },
  warnings: { warn: true },
  drafts: { statuses: ['draft'] },
  p50OutHours: {},
  p50InHours: {}
};

const TOP = 20;
const PAGE = 50;
const ROUNDED: [number, number, number, number] = [0, 3, 3, 0];
const KIND_NAMES: Record<string, string> = { pharmacy: 'аптека', warehouse: 'склад' };

const metricValue = (p: PharmacyStats, m: Metric): number | null => p[m];
const fmtPeriod = (p: Period) => `${fmtDay(p.from)} – ${fmtDay(p.to)}`;

type RankPoint = PharmacyStats & { label: string };

const RankTip: React.FC<TooltipInjected<RankPoint> & { prevLabel?: string }> = props => {
  const p = tooltipPoint(props);
  if (!p) return null;
  const d = deltaPct(p.total, p.prevTotal);
  const rows: TipRow[] = [
    { label: 'Документов', value: fmtInt(p.total), bold: true, hint: d === null ? 'в прошлом периоде не было' : `${fmtDelta(d)} к прошлому периоду (${fmtInt(p.prevTotal)})` },
    { label: 'Отправлено', value: fmtInt(p.sent), color: KIND_COLORS.direct },
    { label: 'Получено', value: fmtInt(p.received), color: KIND_COLORS.return },
    { label: 'Возвратов отправлено', value: fmtInt(p.returns) },
    { label: 'Через РЦ', value: fmtInt(p.via), color: KIND_COLORS.via },
    { label: 'Приняты и завершены', value: fmtInt(p.done), color: GROUP_COLORS.done, divider: true, hint: fmtPct(share(p.done, p.total)) },
    { label: 'С ошибками', value: fmtInt(p.errors), color: GROUP_COLORS.error },
    { label: 'В работе', value: fmtInt(p.active), color: GROUP_COLORS.active },
    { label: 'Черновики', value: fmtInt(p.drafts), color: GROUP_COLORS.draft },
    { label: 'На контроле', value: fmtInt(p.warnings), color: WATCH_COLORS.warn },
    { label: 'Медиана отправки', value: fmtHours(p.p50OutHours), divider: true },
    { label: 'Медиана приёмки', value: fmtHours(p.p50InHours) }
  ];
  return (
    <TipBox
      width={300} title={p.name} subtitle={[p.kind ? KIND_NAMES[p.kind] : null, p.region].filter(Boolean).join(' · ') || undefined}
      rows={rows} footer={props.prevLabel ? `Прошлый период: ${props.prevLabel}. Клик - открыть документы аптеки` : 'Клик - открыть документы аптеки'}
    />
  );
};

const RankChart: React.FC<{ data: PharmaciesResponse; onPick: (p: PharmacyStats, m: Metric) => void }> = ({ data, onPick }) => {
  const [metric, setMetric] = useState<Metric>('total');
  const info = METRICS.find(m => m.value === metric) || METRICS[0];
  const hours = metric === 'p50OutHours' || metric === 'p50InHours';
  const points = useMemo<RankPoint[]>(() => data.pharmacies
    .filter(p => { const v = metricValue(p, metric); return v !== null && v > 0; })
    .slice()
    .sort((a, b) => (metricValue(b, metric) || 0) - (metricValue(a, metric) || 0))
    .slice(0, TOP)
    .map(p => ({ ...p, label: p.name })),
  [data.pharmacies, metric]);
  const choose = (item: { payload?: unknown }) => {
    const p = item.payload as RankPoint | undefined;
    if (p) onPick(p, metric);
  };
  const bar = (key: string, fill: string, stack?: string, last = true) => (
    <Bar
      dataKey={key} stackId={stack} fill={fill} radius={last ? ROUNDED : 0} isAnimationActive={false}
      onClick={choose} style={{ cursor: 'pointer' }}
    />
  );

  return (
    <ChartCard
      wide title={`Топ-${Math.min(TOP, points.length) || TOP} ${info.title}`} subtitle={`${info.subtitle} Клик - открыть документы аптеки.`}
      actions={<MiniToggle<Metric> value={metric} onChange={setMetric} options={METRICS.map(m => ({ value: m.value, label: m.label }))} />}
    >
      {!points.length ? <NoData height={200} text={hours ? 'Нет завершённых документов' : 'Таких документов нет'} /> : (
        <Box sx={{ height: points.length * (metric === 'total' ? 34 : 26) + 36 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={points} layout="vertical" margin={{ top: 0, right: 16, left: 0, bottom: 0 }} barCategoryGap={4} barGap={1}>
              <CartesianGrid stroke={CHART.grid} horizontal={false} />
              <XAxis
                type="number" tick={AXIS_TICK} axisLine={{ stroke: BORDER }} tickLine={false} allowDecimals={hours}
                tickFormatter={v => (hours ? fmtHours(Number(v)) : fmtInt(Number(v)))}
              />
              <YAxis type="category" dataKey="label" width={210} tick={{ ...AXIS_TICK, fill: '#334155' }} axisLine={false} tickLine={false} interval={0} tickFormatter={v => clip(String(v), 32)} />
              <ChartTooltip content={<RankTip prevLabel={fmtPeriod(data.prevPeriod)} />} cursor={{ fill: 'rgba(148,163,184,0.12)' }} />
              {metric === 'total' && bar('sent', KIND_COLORS.direct, 's', false)}
              {metric === 'total' && bar('received', KIND_COLORS.return, 's')}
              {metric === 'total' && bar('prevTotal', '#cbd5e1')}
              {metric !== 'total' && bar(metric, METRIC_COLORS[metric])}
            </BarChart>
          </ResponsiveContainer>
        </Box>
      )}
    </ChartCard>
  );
};

type SortKey = 'name' | 'total' | 'delta' | 'sent' | 'received' | 'returns' | 'via' | 'done' | 'errors' | 'active' | 'drafts' | 'warnings' | 'p50OutHours' | 'p50InHours';

const sortValue = (p: PharmacyStats, key: SortKey): number | string | null => {
  if (key === 'delta') return deltaPct(p.total, p.prevTotal);
  return p[key];
};

/** Сравнение с пустыми значениями в конце при любом направлении */
function compare(a: number | string | null, b: number | string | null, dir: 1 | -1): number {
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  if (typeof a === 'number' && typeof b === 'number') return (a - b) * dir;
  return String(a).localeCompare(String(b), 'ru', { numeric: true }) * dir;
}

const COLUMNS: Array<{ key: SortKey; label: string; align?: 'right'; tooltip?: string }> = [
  { key: 'name', label: 'Аптека / склад' },
  { key: 'total', label: 'Документов', align: 'right' },
  { key: 'delta', label: 'К прошлому периоду', align: 'right' },
  { key: 'sent', label: 'Отправлено', align: 'right' },
  { key: 'received', label: 'Получено', align: 'right' },
  { key: 'returns', label: 'Возвраты', align: 'right', tooltip: 'Возвраты, где аптека - отправитель' },
  { key: 'via', label: 'Через РЦ', align: 'right' },
  { key: 'done', label: 'Завершены', align: 'right' },
  { key: 'errors', label: 'Ошибки', align: 'right' },
  { key: 'active', label: 'В работе', align: 'right' },
  { key: 'drafts', label: 'Черновики', align: 'right' },
  { key: 'warnings', label: 'На контроле', align: 'right' },
  { key: 'p50OutHours', label: 'Медиана отправки', align: 'right', tooltip: 'Медиана от создания до «Завершен», аптека - отправитель' },
  { key: 'p50InHours', label: 'Медиана приёмки', align: 'right', tooltip: 'Медиана от создания до «Завершен», аптека - получатель' }
];

const ASC_FIRST: SortKey[] = ['name'];

/** Число с цветом, если не ноль */
const Count: React.FC<{ value: number; color: string }> = ({ value, color }) => (
  <Box component="span" sx={{ color: value ? color : '#cbd5e1', fontWeight: value ? 600 : 400 }}>{value ? fmtInt(value) : '-'}</Box>
);

const PharmaciesTable: React.FC<{ data: PharmaciesResponse; onPick: (p: PharmacyStats, m: Metric) => void }> = ({ data, onPick }) => {
  const [query, setQuery] = useState('');
  const [kind, setKind] = useState<KindFilter>('all');
  const [sort, setSort] = useState<{ key: SortKey; dir: 1 | -1 }>({ key: 'total', dir: -1 });
  const [shown, setShown] = useState(PAGE);
  const maxTotal = data.pharmacies.reduce((m, p) => Math.max(m, p.total), 0);
  const hasWarehouses = data.pharmacies.some(p => p.kind === 'warehouse');

  const rank = useMemo(() => {
    const map: Record<string, number> = {};
    data.pharmacies.forEach((p, i) => { map[p.guid] = i + 1; });
    return map;
  }, [data.pharmacies]);

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = data.pharmacies.filter(p => (kind === 'all' || p.kind === kind)
      && (!q || `${p.name} ${p.region || ''}`.toLowerCase().indexOf(q) !== -1));
    return list.sort((a, b) => compare(sortValue(a, sort.key), sortValue(b, sort.key), sort.dir));
  }, [data.pharmacies, query, kind, sort]);

  const toggleSort = (key: SortKey) => {
    setSort(s => (s.key === key ? { key, dir: s.dir === 1 ? -1 : 1 } : { key, dir: ASC_FIRST.indexOf(key) !== -1 ? 1 : -1 }));
    setShown(PAGE);
  };
  const cell = (p: PharmacyStats, m: Metric) => (e: React.MouseEvent) => { e.stopPropagation(); onPick(p, m); };

  return (
    <ChartCard
      wide title="Все аптеки и склады"
      subtitle={`${fmtInt(data.pharmacies.length)} ${plural(data.pharmacies.length, 'участник', 'участника', 'участников')} документов периода. Клик по строке - документы аптеки, по числу ошибок, черновиков или «на контроле» - только они.`}
      actions={(
        <>
          {hasWarehouses && (
            <MiniToggle<KindFilter>
              value={kind} onChange={v => { setKind(v); setShown(PAGE); }}
              options={[{ value: 'all', label: 'Все' }, { value: 'pharmacy', label: 'Аптеки' }, { value: 'warehouse', label: 'Склады' }]}
            />
          )}
          <TextField
            size="small" placeholder="Название, регион" value={query}
            onChange={e => { setQuery(e.target.value); setShown(PAGE); }}
            sx={{ width: 240, '& .MuiInputBase-input': { fontSize: '0.8rem', py: 0.75 } }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: '#94a3b8' }} /></InputAdornment> }}
          />
        </>
      )}
    >
      {!rows.length ? <NoData height={120} text={query || kind !== 'all' ? 'Ничего не найдено' : undefined} /> : (
        <>
          <TableContainer sx={{ border: `1px solid ${BORDER}`, borderRadius: 1.5, maxHeight: 640 }}>
            <Table size="small" stickyHeader sx={TABLE_SX}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 32 }}>№</TableCell>
                  {COLUMNS.map(c => (
                    <TableCell key={c.key} align={c.align} title={c.tooltip} sortDirection={sort.key === c.key ? (sort.dir === 1 ? 'asc' : 'desc') : false}>
                      <TableSortLabel active={sort.key === c.key} direction={sort.key === c.key && sort.dir === 1 ? 'asc' : 'desc'} onClick={() => toggleSort(c.key)}>
                        {c.label}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.slice(0, shown).map(p => {
                  const d = deltaPct(p.total, p.prevTotal);
                  return (
                    <TableRow key={p.guid} hover onClick={() => onPick(p, 'total')} sx={{ cursor: 'pointer' }}>
                      <TableCell sx={{ color: '#94a3b8 !important' }}>{rank[p.guid]}</TableCell>
                      <TableCell sx={{ maxWidth: 260 }}>
                        <Typography sx={{ fontSize: '0.78rem', fontWeight: 600 }} noWrap title={p.name}>{p.name}</Typography>
                        <SubText>{[p.kind ? KIND_NAMES[p.kind] : null, p.region].filter(Boolean).join(' · ') || '-'}</SubText>
                      </TableCell>
                      <TableCell align="right" sx={{ minWidth: 130 }}>
                        <ShareBar value={p.total} max={maxTotal} label={fmtInt(p.total)} />
                      </TableCell>
                      <TableCell align="right" sx={{ color: d === null ? undefined : `${d >= 0 ? '#15803d' : '#b91c1c'} !important` }}>
                        {d === null ? 'новые' : fmtDelta(d)}
                        <SubText>{p.prevTotal ? `было ${fmtInt(p.prevTotal)}` : 'документов не было'}</SubText>
                      </TableCell>
                      <TableCell align="right">{fmtInt(p.sent)}</TableCell>
                      <TableCell align="right">{fmtInt(p.received)}</TableCell>
                      <TableCell align="right">{fmtInt(p.returns)}</TableCell>
                      <TableCell align="right">{fmtInt(p.via)}</TableCell>
                      <TableCell align="right">
                        {fmtInt(p.done)}
                        <SubText>{fmtPct(share(p.done, p.total), 0)}</SubText>
                      </TableCell>
                      <TableCell align="right" onClick={p.errors ? cell(p, 'errors') : undefined}><Count value={p.errors} color={GROUP_COLORS.error} /></TableCell>
                      <TableCell align="right">{fmtInt(p.active)}</TableCell>
                      <TableCell align="right" onClick={p.drafts ? cell(p, 'drafts') : undefined}><Count value={p.drafts} color="#b45309" /></TableCell>
                      <TableCell align="right" onClick={p.warnings ? cell(p, 'warnings') : undefined}><Count value={p.warnings} color={WATCH_COLORS.warn} /></TableCell>
                      <TableCell align="right">{fmtHours(p.p50OutHours)}</TableCell>
                      <TableCell align="right">{fmtHours(p.p50InHours)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          {rows.length > shown && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
              <Button size="small" onClick={() => setShown(s => s + PAGE)} sx={{ textTransform: 'none' }}>
                Показать ещё {Math.min(PAGE, rows.length - shown)} из {fmtInt(rows.length - shown)}
              </Button>
            </Box>
          )}
        </>
      )}
    </ChartCard>
  );
};

/** Медиана списка чисел */
function median(values: number[]): number | null {
  if (!values.length) return null;
  const v = values.slice().sort((a, b) => a - b);
  const mid = Math.floor(v.length / 2);
  return v.length % 2 ? v[mid] : (v[mid - 1] + v[mid]) / 2;
}

/** Аптеки и склады: обе стороны документов периода, рейтинг и полная таблица */
const PharmaciesView: React.FC<ViewProps> = ({ params, paramsKey, active, reloadToken, onCohort }) => {
  const state = useLoad(
    active ? (_silent: boolean, signal: AbortSignal) => analyticsApi.pharmacies(params, signal) : null,
    `pharmacies|${paramsKey}`, reloadToken, 'Не удалось построить аналитику по аптекам'
  );
  const data = state.data;

  const stats = useMemo<Stat[]>(() => {
    if (!data) return [];
    const list = data.pharmacies;
    const pharmacies = list.filter(p => p.kind === 'pharmacy').length;
    const warehouses = list.filter(p => p.kind === 'warehouse').length;
    const totals = list.map(p => p.total);
    const withErrors = list.filter(p => p.errors > 0);
    const withWarnings = list.filter(p => p.warnings > 0);
    const fresh = list.filter(p => !p.prevTotal).length;
    const slowest = list.reduce<PharmacyStats | null>((b, p) => (p.p50OutHours !== null && (!b || (b.p50OutHours || 0) < p.p50OutHours) ? p : b), null);
    const top = list[0];
    return [
      { label: 'Участников', value: fmtInt(list.length), hint: `аптек ${fmtInt(pharmacies)} · складов ${fmtInt(warehouses)}` },
      {
        label: 'Документов на участника', value: fmtInt(median(totals)), hint: `в среднем ${(list.length ? totals.reduce((s, v) => s + v, 0) / list.length : 0).toLocaleString('ru-RU', { maximumFractionDigits: 1 })}`,
        tooltip: 'Медиана: у половины аптек и складов документов меньше, у половины - больше'
      },
      { label: 'Самый активный', value: top ? fmtInt(top.total) : '-', hint: top ? clip(top.name, 28) : undefined },
      {
        label: 'С ошибками', value: fmtInt(withErrors.length), color: withErrors.length ? GROUP_COLORS.error : undefined,
        hint: `${fmtInt(withErrors.reduce((s, p) => s + p.errors, 0))} документов`
      },
      {
        label: 'С документами на контроле', value: fmtInt(withWarnings.length), color: withWarnings.length ? WATCH_COLORS.warn : undefined,
        hint: `${fmtInt(withWarnings.reduce((s, p) => s + p.warnings, 0))} документов`
      },
      {
        label: 'Медленнее всех отправляет', value: slowest ? fmtHours(slowest.p50OutHours) : '-', hint: slowest ? clip(slowest.name, 28) : 'нет завершённых',
        tooltip: 'Наибольшая медиана от создания до «Завершен» по документам, где аптека - отправитель'
      },
      { label: 'Новые участники', value: fmtInt(fresh), tooltip: `Не было документов в прошлом периоде (${fmtPeriod(data.prevPeriod)})` }
    ];
  }, [data]);

  const pick = (p: PharmacyStats, m: Metric) => onCohort({ pharmacy: [p.guid], ...METRIC_PATCH[m] });

  if (!data) {
    return (
      <ChartCard wide title="Аптеки" loading={state.loading} error={state.error ? state.error.text : null} onRetry={state.retry}>
        {!state.error && <ChartSkeleton height={420} />}
      </ChartCard>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, opacity: state.loading ? 0.65 : 1, transition: 'opacity 0.15s' }}>
      {state.error && (
        <Alert severity="error" action={<Button color="inherit" size="small" onClick={state.retry}>Повторить</Button>}>
          {state.error.text}. На экране - данные прежнего запроса.
        </Alert>
      )}
      {!data.pharmacies.length ? (
        <ChartCard wide title="Аптеки"><NoData height={200} text="В периоде нет документов по выбранным фильтрам" /></ChartCard>
      ) : (
        <>
          <StatStrip items={stats} />
          <RankChart data={data} onPick={pick} />
          <PharmaciesTable data={data} onPick={pick} />
        </>
      )}
    </Box>
  );
};

export default React.memo(PharmaciesView);
